<template>
  <div>
    <v-card elevation="0" class="px-7">
      <v-row dense align="center" justify="space-between">
        <v-col cols="12" sm="6">
          <h1 class="fs-16 primary--text font-weight-bold">
            Request
          </h1>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-breadcrumbs class="app-breadcrumbs py-3" :items="breadcrumbs" />
        </v-col>
      </v-row>
    </v-card>

    <v-container class="px-7" fluid>
      <v-row no-gutters align="center" justify="space-between" class="mb-3">
        <v-col cols="12" sm="6">
          <h1 class="fs-20 font-weight-bold bluedarken--text">
            Request
          </h1>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn color="primary" icon large>
            <v-icon
              @click="syncData"
              color="bluedarken"
              class="icon-bold icon-large"
              large
            >
              mdi-refresh
            </v-icon>
          </v-btn>
          <v-btn color="primary" icon large>
            <v-icon
              @click="copyData"
              color="bluedarken"
              class="icon-bold icon-large"
              large
            >
              file_copy
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <v-card :loading="loading" class="elevation-card rounded-12 mb-5">
        <v-card-title>
          <v-icon color="primary2" class="mr-2">
            mdi-web
          </v-icon>
          <span class="fs-14 font-weight-bold primary2--text">
            Request
          </span>
          <v-spacer />
          <select-column-filter v-model="columnActive" :headers="headers" />
          <v-btn
            to="/sshcf/requests/create"
            depressed
            dark
            color="menuActive"
            class="btn-medium mr-2"
          >
            Add New Request
          </v-btn>
          <v-btn
            depressed
            dark
            color="menuActive"
            class="btn-medium"
            @click="download"
          >
            Download
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-simple-table
            fixed-header
            class="table-taxonomy nav-table sshcf-table"
          >
            <template v-slot:default>
              <VuePerfectScrollbar
                class="drawer-menu--scroll"
                :settings="scrollSettings"
              >
                <thead>
                  <tr>
                    <template v-for="(header, index) in headers">
                      <th
                        v-if="isHeaderSelected(header)"
                        :key="header.text"
                        class="white--text"
                        :class="{
                          [`text-${header.align}`]: true,
                          [header.customClass]: !!header.customClass
                        }"
                      >
                        <span>{{ header.text }}</span>
                        <filter-column
                          v-if="header.value !== 'action'"
                          :indexItem="index"
                          :field="header.value"
                          :listFilter="getFiltered(header.value, index)"
                          @sortAsc="getNestedSort"
                          @searchColumn="searchCln"
                          @filterItem="searchCln"
                        />
                      </th>
                    </template>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    @click="onRowClicked(item)"
                    v-for="item in filteredItems"
                    :key="item.name"
                    class="hoverable-tr"
                  >
                    <td
                      v-if="isColumnSelected('hcf_code')"
                      class="sticky-body1"
                    >
                      <router-link
                        :to="`/sshcf/request/${item.form_id}`"
                        class="bluedarken--text text-link"
                      >
                        {{ item.hcf_code }}
                      </router-link>
                    </td>
                    <td v-if="isColumnSelected('preval_number')">
                      {{ item.preval_number }}
                    </td>
                    <td v-if="isColumnSelected('io_number')">
                      {{ item.io_number }}
                    </td>
                    <td v-if="isColumnSelected('house')">
                      {{ item.house }}
                    </td>
                    <td v-if="isColumnSelected('region')">{{ item.region }}</td>
                    <td v-if="isColumnSelected('district')">
                      {{ item.district }}
                    </td>
                    <td v-if="isColumnSelected('vendor_code')">
                      {{ item.vendor_code }}
                    </td>
                    <td v-if="isColumnSelected('hospital')">
                      {{ item.hospital }}
                    </td>
                    <td v-if="isColumnSelected('support_amount')">
                      {{ item.support_amount | toCurrencyWithoutSign }}
                    </td>
                    <td v-if="isColumnSelected('last_approval')">
                      {{ item.last_approval }}
                    </td>
                    <td v-if="isColumnSelected('created_date')">
                      {{ item.created_date }}
                    </td>
                    <td v-if="isColumnSelected('local_status')">
                      {{ item.local_status }}
                    </td>
                    <td
                      v-if="isColumnSelected('action') && isHNUser"
                      class="text-center"
                    >
                      <dialog-detail-request
                        v-if="canViewDetail(item)"
                        :id="item.form_id"
                      >
                        <template #activator="{on}">
                          <v-btn
                            text
                            color="primary"
                            class="text-capitalize font-weight-bold"
                            v-on="on"
                          >
                            <v-icon class="mr-1">mdi-text-box</v-icon>
                            Complete
                          </v-btn>
                        </template>
                      </dialog-detail-request>
                    </td>
                  </tr>
                  <tr v-if="!filteredItems.length">
                    <td :colspan="headers.length" class="text-center">
                      {{ loading ? 'Loading...' : 'No data available' }}
                    </td>
                  </tr>
                </tbody>
              </VuePerfectScrollbar>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <v-dialog v-model="dialog" max-width="800px">
        <card-progress-tracking
          v-if="formRequest"
          :formRequest="formRequest"
          :loading="loadingDetail"
          :steps="steps"
        />
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import CardProgressTracking from '@/components/SSHCF/CardProgressTracking';
import { exportToExcel, formatDate } from '@/utils/helpers';
import FilterColumn from '@/components/filterColumn';
import DialogDetailRequest from '@/components/SSHCF/DialogDetailRequest';
import SelectColumnFilter from '@/components/SelectColumnFilter';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import { exportValue } from '@/mixins/filter';
import moment from 'moment';

export default {
  components: {
    SelectColumnFilter,
    DialogDetailRequest,
    FilterColumn,
    CardProgressTracking,
    VuePerfectScrollbar
  },
  data: () => ({
    scrollSettings: {
      maxScrollbarLength: 340
    },
    columnActive: [],
    breadcrumbs: [
      {
        text: 'SSHCF',
        to: '/sshcf/'
      },
      {
        text: 'Request',
        to: '/sshcf/requests/'
      }
    ],
    totalItems: 0,
    options: {
      itemsPerPage: 10,
      page: 1
    },
    loadingDetail: false,
    items: [],
    filter: null,
    loading: false,
    dialog: false
  }),
  computed: {
    ...mapState('sshcf', {
      // items: 'formRequests',
      formRequest: 'formRequest'
    }),
    headers() {
      const actions = [
        {
          text: 'Completion',
          align: 'center',
          value: 'action',
          exportable: true
        }
      ];
      return [
        {
          text: 'SSHCF Code',
          align: 'left',
          value: 'hcf_code',
          customClass: 'sticky-header1',
          exportable: true
        },
        {
          text: 'Preval Number',
          align: 'left',
          value: 'preval_number',
          exportable: true
        },
        {
          text: 'IO Number',
          align: 'left',
          value: 'io_number',
          exportable: true
        },
        {
          text: 'House',
          align: 'left',
          value: 'house',
          exportable: true
        },
        {
          text: 'Region',
          align: 'left',
          value: 'region',
          exportable: true
        },
        {
          text: 'District',
          align: 'left',
          value: 'district',
          exportable: true
        },
        {
          text: 'Vendor Code',
          align: 'left',
          value: 'vendor_code',
          exportable: true
        },
        {
          text: 'Hospital Name',
          align: 'left',
          value: 'hospital',
          exportable: true
        },
        {
          text: 'Amount of Support',
          align: 'left',
          value: 'support_amount',
          formatter: val => +val,
          exportable: true
        },
        {
          text: 'Current Pending Approver',
          align: 'left',
          value: 'last_approval',
          // formatter: val => this.lastApproval(val || []),
          exportable: true
        },
        {
          text: 'Created Date',
          align: 'left',
          value: 'created_date',
          exportable: true
        },
        {
          text: 'Status',
          align: 'left',
          value: 'local_status',
          exportable: true
        },
        ...(this.isHNUser ? actions : [])
      ];
    },
    // items: {
    //   get() {
    //     return this.$store.getters['sshcf/formRequests'];
    //   },
    //   set(value) {
    //     this.$store.commit('sshcf/set', {
    //       formRequests: value
    //     });
    //   }
    // },
    filteredItems() {
      return this.getFilteredItems();
    },
    steps() {
      return this.formRequest?.approvals.map(item => ({
        name: item.mapping_user ? item.mapping_user.role : '-',
        email: item.user_email,
        complete: item.status === 'Approved',
        date: formatDate(item.updated_at),
        status: item.status
      }));
    },
    isSelectedAll: {
      get() {
        return this.headers
          ? this.columnActive.length === this.headers.length
          : false;
      },
      set(value) {
        const selected = [];

        if (value) {
          this.headers.forEach(function(user) {
            selected.push(user);
          });
        }

        this.columnActive = selected;
      }
    }
  },
  watch: {
    options(options) {
      const { itemsPerPage, page } = options;
      this.fetch({ page, limit: itemsPerPage });
    }
  },
  async mounted() {
    await this.selectAllColumn();
    this.fetch();
  },
  methods: {
    getApprovalEmail(approvals) {
      return approvals[0]?.user_email || '';
    },
    lastApproval(formRequest) {
      let { local_status, approvals = [] } = formRequest;
      if (local_status === 'Submitted') {
        return this.getApprovalEmail(approvals);
      } else if (local_status === 'Approval Process') {
        return this.getApprovalEmail(
          approvals.filter(item => item.status === null)
        );
      }
      return '';
    },
    canViewDetail(item) {
      return ['Fully Approved', 'Completing Doc'].includes(item.local_status);
    },
    async syncData() {
      this.loading = true;
      await this.$store.dispatch('sshcf/syncData', 'form_request');
      this.fetch();
    },
    copyData() {
      this.loading = true;
      this.$store.dispatch('sshcf/copyData', 'form_requests');
      this.fetch();
    },
    selectAllColumn() {
      this.columnActive = this.headers;
    },
    async getDetail(id) {
      this.dialog = true;
      this.loadingDetail = true;
      await this.$store.dispatch('sshcf/getFormRequestDetail', id);
      this.loadingDetail = false;
    },
    async fetch(params = {}) {
      this.loading = true;
      const res = await this.$store.dispatch('sshcf/getFormRequests', {
        ...params
      });
      this.loading = false;
      this.items = res.data.map(item => ({
        ...item,
        created_date: formatDate(item.created_date),
        last_approval: this.lastApproval(item)
      }));
      this.totalItems = res.data.total_data;
    },
    onRowClicked(item) {
      this.getDetail(item.form_id);
    },
    toggleSelectAll() {
      this.isSelectAll = !this.isSelectAll;
    },
    isHeaderSelected(header) {
      return this.columnActive.map(item => item.value).includes(header.value);
    },
    isColumnSelected(name) {
      return this.columnActive.filter(item => item.value === name).length > 0;
    },
    getAscSort(obj) {
      this.items.sort(this.dynamicSort(obj.fieldName));
    },
    getNestedSort(obj) {
      this.nestedSort(obj.fieldName, this.items);
    },
    searchCln(obj) {
      this.filter = obj;
    },
    getFilteredItems() {
      const obj = this.filter;
      let arr = this.items;

      if (!obj) return arr;

      let nameField = obj.fieldName;
      let valSearch = Array.isArray(obj.value)
        ? obj.value.join('|').toLowerCase()
        : obj.value.toLowerCase();
      let result = [];
      if (!nameField.includes('.')) {
        /*search array of object*/
        if (nameField === 'start') {
          nameField = 'dateStart';
        } else if (nameField === 'end') {
          nameField = 'dateEnd';
        }
        result = arr.filter(function(a) {
          if (a[nameField] !== null) {
            if (Array.isArray(obj.value)) {
              return obj.value.includes(String(a[nameField]));
            } else {
              return a[nameField]
                .toString()
                .toLowerCase()
                .match(valSearch);
            }
          }
        });
      } else {
        /*search array of nested object*/
        let arrObj = nameField.split('.');
        if (arrObj.length === 2) {
          result = arr.filter(function(a) {
            if (a[arrObj[0]][arrObj[1]] !== null) {
              if (Array.isArray(obj.value)) {
                return obj.value.includes(a[arrObj[0]][arrObj[1]].toString());
              } else {
                return a[arrObj[0]][arrObj[1]]
                  .toString()
                  .toLowerCase()
                  .match(valSearch);
              }
            }
          });
        } else if (arrObj.length === 3) {
          result = arr.filter(function(a) {
            if (a[arrObj[0]][arrObj[1]][arrObj[2]] !== null) {
              if (Array.isArray(obj.value)) {
                return obj.value.includes(
                  a[arrObj[0]][arrObj[1]][arrObj[2]].toString()
                );
              } else {
                return a[arrObj[0]][arrObj[1]][arrObj[2]]
                  .toString()
                  .toLowerCase()
                  .match(valSearch);
              }
            }
          });
        } else if (arrObj.length === 4) {
          result = arr.filter(function(a) {
            if (a[arrObj[0]][arrObj[1]][arrObj[2]][arrObj[3]] !== null) {
              if (Array.isArray(obj.value)) {
                return obj.value.includes(
                  a[arrObj[0]][arrObj[1]][arrObj[2]][arrObj[3]].toString()
                );
              } else {
                return a[arrObj[0]][arrObj[1]][arrObj[2]][arrObj[3]]
                  .toString()
                  .toLowerCase()
                  .match(valSearch);
              }
            }
          });
        } else {
          result = this.items;
        }
      }

      return result;
    },
    getFiltered(name) {
      if (!name.includes('.')) {
        return Object.keys(
          this.items.reduce((result, arr) => {
            result[arr[name]] = [...(result[arr[name]] || []), arr];
            return result;
          }, {})
        );
      } else {
        let nameField = name.split('.');
        return Object.keys(
          this.items.reduce((result, arr) => {
            if (!arr[nameField[0]]) return;
            result[arr[nameField[0]][nameField[1]]] = [
              ...(result[arr[nameField[0]][nameField[1]]] || []),
              arr
            ];
            return result;
          }, {})
        );
      }
    },
    download() {
      const headers = this.headers.filter(item => item.exportable);
      const columns = headers.map(item => item.text);
      const items = this.filteredItems.map(item =>
        headers.map(header => exportValue(header, item))
      );
      const date = moment().format('YYYY-MM-DD-HHmmss');
      exportToExcel(columns, items, 'Form-Requests-' + date);
    }
  }
};
</script>

<style lang="scss" scoped>
.hoverable-tr {
  cursor: pointer;

  &:hover {
    background: #dfdfdf;
  }
}

.drawer-menu--scroll {
  max-height: 600px;
  overflow: auto;
}
</style>
